import React, { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, History, Clock, Download } from 'lucide-react';
import { api } from '../../context/AuthContext';

const dt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; } };
const Box = ({ titel, icon: Icon, testId, children }) => (
  <section className="bg-white border border-gray-200 rounded-2xl p-5" data-testid={testId}>
    <h3 className="flex items-center gap-2 text-sm font-medium text-[#1B3A5C] mb-3"><Icon className="w-4 h-4 text-[#b8893d]" /> {titel}</h3>
    {children}
  </section>
);

export const MandatsendeKarte = ({ sb, mnr, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const me = sb.mandatsende;
  const ausfuehren = async (pfad, frage) => {
    if (!window.confirm(frage)) return;
    setBusy(true); setMsg('');
    try { await api.post(`/register/mandanten/${mnr}/${pfad}`, {}); onDone(); } catch (e) { setMsg(e?.response?.data?.detail || 'Aktion fehlgeschlagen'); } finally { setBusy(false); }
  };
  return (
    <Box titel="Mandat" icon={ShieldCheck} testId="stammblatt-mandatsende">
      {me ? (
        <div className="text-xs space-y-2">
          <p data-testid="mandatsende-status">Mandat beendet am {dt(me.am)} von {me.von}. Portal nur lesen bis <b>{dt(me.nur_lesen_bis)}</b>{me.inaktiv_gesetzt ? ', danach inaktiv gesetzt' : ', danach automatisch inaktiv'}.</p>
          <button type="button" disabled={busy} onClick={() => ausfuehren('mandat-wiederaufnehmen', 'Mandat wieder aufnehmen? Das Portal ist danach wieder voll nutzbar.')} className="h-8 px-3 rounded-[10px] border border-[#1B3A5C] text-[#1B3A5C] text-xs" data-testid="mandat-wiederaufnehmen">Wieder aufnehmen</button>
        </div>
      ) : (
        <div className="text-xs space-y-2">
          <p>„Mandat beenden“ schließt alle Akten, sendet eine Abschlussnachricht und stellt das Portal für die eingestellte Zeit auf „nur lesen“. Danach wird der Mandant automatisch inaktiv.</p>
          <button type="button" disabled={busy} onClick={() => ausfuehren('mandat-beenden', 'Mandat wirklich beenden? Alle Akten werden geschlossen.')} className="h-8 px-3 rounded-[10px] bg-rose-700 text-white text-xs" data-testid="mandat-beenden">Mandat beenden</button>
        </div>
      )}
      {msg && <p className="text-xs text-rose-700 mt-2">{msg}</p>}
    </Box>
  );
};

export const Zugriffe = ({ mnr }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/register/mandanten/${mnr}/zugriffe`).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [mnr]);
  if (!items) return <Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" />;
  return (
    <Box titel={`Zugriffe (${items.length})`} icon={History} testId="mandant-zugriffe">
      {!items.length ? <p className="text-xs text-gray-500">Noch keine Zugriffe protokolliert.</p> : (
        <ul className="divide-y divide-gray-100 text-xs">
          {items.map((z, i) => <li key={i} className="py-2 grid grid-cols-[130px_1fr_90px_1fr] gap-2" data-testid="zugriff-zeile"><span className="text-gray-500">{dt(z.am)}</span><span className="text-[#1B3A5C]">{z.name}</span><span className="text-gray-500" data-testid="zugriff-rolle">{z.rolle || 'Kanzlei'}</span><span>{z.bereich}{z.case_id ? ` · ${z.case_id}` : ''}</span></li>)}
        </ul>
      )}
    </Box>
  );
};

const hm = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')} Std.`;
const monatName = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

export const ZeitKarte = ({ mnr }) => {
  const jetzt = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' }).slice(0, 7);
  const [monat, setMonat] = useState(jetzt);
  const [z, setZ] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/register/mandanten/${mnr}/zeit`, { params: { monat } }).then((r) => setZ(r.data)).catch(() => setZ(false)); }, [mnr, monat]);
  const pdf = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/register/mandanten/${mnr}/zeit.pdf`, { params: { monat }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `Zeitbericht_${mnr}_${monat}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };
  if (z === false) return null;
  const monate = z ? Array.from(new Set([jetzt, ...z.monate])).sort().reverse() : [jetzt];
  return (
    <Box titel="Bearbeitungszeit" icon={Clock} testId="stammblatt-zeit">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={monat} onChange={(e) => setMonat(e.target.value)} className="h-8 border border-gray-300 rounded-[10px] px-2 text-sm" data-testid="zeit-monat">
          {monate.map((m) => <option key={m} value={m}>{monatName(m)}</option>)}
        </select>
        <span className="text-sm text-[#1B3A5C]" data-testid="zeit-gesamt">Gesamt: <b>{z ? hm(z.gesamt) : '…'}</b></span>
        <button type="button" onClick={pdf} disabled={busy || !z} className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-[10px] border border-[#1B3A5C] text-[#1B3A5C] text-xs hover:bg-[#1B3A5C] hover:text-white transition-colors disabled:opacity-50" data-testid="zeit-pdf">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Monatsübersicht (PDF)
        </button>
      </div>
      {!z ? <Loader2 className="w-4 h-4 animate-spin text-[#b8893d]" /> : !z.personen.length ? <p className="text-xs text-gray-500">In diesem Monat wurde keine Zeit erfasst.</p> : (
        <ul className="divide-y divide-gray-100 text-xs">{z.personen.map((p) => <li key={p.person} className="py-1.5 flex justify-between" data-testid="zeit-person"><span className="text-[#1B3A5C]">{p.person}</span><span className="tabular-nums">{hm(p.minuten)}</span></li>)}</ul>
      )}
    </Box>
  );
};
